import React, { EventHandler } from 'react';
import { TestResult } from '@ecosconnect/core/src/types/jobFormData';
import { AnyForm } from '@ecosconnect/core/src/helpers/backflow/form-definitions';
import {
  FieldTypes,
  ValidationStatus,
} from '@ecosconnect/core/src/helpers/backflow/form-builder';
import AccordionItem from '../../Accordion/AccordionItem';
import ButtonToggle from '../../Button/ButtonToggle';
import { Input } from '../../Form';
import { yesNoOptions } from '../options';
import FieldMessage from './FieldMessage';

// Renders every visible field of a test form in declaration order, the same
// way the mobile app does, so the editors can't drift from the definitions.
const TestFieldsEdit: React.FC<{
  test: TestResult;
  form: AnyForm;
  onFormInputChange: EventHandler<any>;
}> = ({ test, form, onFormInputChange }) => (
  <>
    {Object.entries(form.fields).map(([name, field]) => {
      if (!field || field.status === ValidationStatus.HIDE) return null;
      const value = (test as Record<string, unknown>)[name];
      const required = field.status === ValidationStatus.INCOMPLETE;

      switch (field.type.type) {
        case FieldTypes.INTEGER:
        case FieldTypes.DECIMAL: {
          const integer = field.type.type === FieldTypes.INTEGER;
          return (
            <React.Fragment key={name}>
              <AccordionItem
                label={field.label}
                alignItems="center"
                as="label"
                value={
                  <Input
                    required={required}
                    onChange={onFormInputChange}
                    variant="accordion"
                    type="number"
                    name={name}
                    value={value as any}
                    step={integer ? '1' : '0.1'}
                    min="0"
                    max={integer ? '500' : '200'}
                    autoComplete="off"
                  />
                }
              />
              <FieldMessage field={field} />
            </React.Fragment>
          );
        }

        case FieldTypes.BOOLEAN:
          return (
            <AccordionItem
              key={name}
              label={field.label}
              alignItems="center"
              value={
                <ButtonToggle
                  name={name}
                  onClick={onFormInputChange}
                  buttonOptions={yesNoOptions}
                  my={-1}
                  value={typeof value !== 'number' ? 0 : value}
                />
              }
            />
          );

        case FieldTypes.SELECT: {
          // Options labelled "_" are stored values without a button (e.g. the
          // measured RP Check 2), matching the mobile app.
          const options = Object.entries(field.type.options)
            .filter(([label]) => !label.startsWith('_'))
            .map(([label, optionValue]) => ({ label, value: optionValue }));
          const wide = options.length > 2;
          return (
            <AccordionItem
              key={name}
              label={field.label}
              flexDirection={wide ? 'column' : undefined}
              alignItems={wide ? 'flex-start' : 'center'}
              value={
                <ButtonToggle
                  required={required}
                  name={name}
                  onClick={onFormInputChange}
                  buttonOptions={options}
                  {...(wide ? { marginTop: 1 } : { my: -1 })}
                  value={value}
                />
              }
            />
          );
        }
      }
    })}
  </>
);

export default TestFieldsEdit;
